import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import { api, errMsg, fileUrl } from "../../lib/api";

const BANK = [
  ["bank_account_name", "Account name"],
  ["bank_name", "Bank name"],
  ["bank_account_no", "Account number"],
  ["bank_ifsc", "IFSC"],
  ["bank_branch", "Branch"],
];

const LogoPicker = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      onChange((await api.post("/uploads", fd)).data.path);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="company-logo">
      <span className="label">Company logo (shown on PO and challan PDFs)</span>
      <div className="flex items-center gap-3">
        {value ? <img src={fileUrl(value)} alt="Company logo" className="h-16 w-16 rounded-lg border object-contain" data-testid="company-logo-img" />
          : <div className="grid h-16 w-16 place-items-center rounded-lg border border-dashed text-xs text-muted-foreground">No logo</div>}
        <label className="btn btn-outline cursor-pointer" data-testid="company-logo-upload-btn">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} {value ? "Change" : "Upload"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="company-logo-input" />
        </label>
        {value && <button type="button" className="icon-btn hover:!text-destructive" onClick={() => onChange("")} aria-label="Remove logo" data-testid="company-logo-remove-btn"><X size={18} /></button>}
      </div>
    </div>
  );
};

const FIELDS = [
  ["company_name", "Company name *", "input"],
  ["address", "Registered office address", "textarea"],
  ["gstin", "GSTIN", "input"],
  ["phone", "Phone", "input"],
  ["email", "Email", "input"],
  ["deliver_to", "Default deliver-to address", "textarea"],
];

export const CompanyTab = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setF(r.data));
  }, []);
  if (!f) return <p className="text-muted-foreground">Loading…</p>;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/settings", f);
      setF(data);
      toast.success("Company settings saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="panel space-y-4 p-4" data-testid="company-tab">
      <LogoPicker value={f.logo} onChange={(logo) => setF((x) => ({ ...x, logo }))} />
      {FIELDS.map(([k, label, type]) => {
        const props = { className: "field", value: f[k] || "", onChange: (e) => setF({ ...f, [k]: e.target.value }), "data-testid": `company-${k}-input` };
        return (
          <div key={k}>
            <label className="label">{label}</label>
            {type === "textarea" ? <textarea rows={3} {...props} /> : <input required={k === "company_name"} {...props} />}
          </div>
        );
      })}
      <div className="space-y-3" data-testid="company-bank">
        <span className="label">Bank details (printed on PO PDF)</span>
        <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
          {BANK.map(([k, label]) => (
            <div key={k}><label className="label">{label}</label><input className="field" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`company-${k}-input`} /></div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <span className="label">Terms and conditions</span>
        {f.terms.map((t, i) => (
          <div key={i} className="flex gap-2">
            <span className="mono pt-3 text-sm text-muted-foreground">{i + 1}.</span>
            <textarea rows={2} className="field" value={t} placeholder="(blank)"
              onChange={(e) => setF({ ...f, terms: f.terms.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`company-term-${i + 1}-input`} />
          </div>
        ))}
      </div>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="company-save-btn">{busy && <Loader2 size={18} className="animate-spin" />} Save settings</button>
    </form>
  );
};
